/**
 * Microservices Configuration
 * Tạo API instances cho từng service với authentication tự động
 */

import { HttpClient } from '../../api/https/client';
import { getServiceUrl, publicEnv, SERVICES } from '@/config';
import { getAccessToken } from '../auth/token-manager';
import { refreshSessionToken } from '../auth/token-manager';
import { handleLogout } from '../auth/token-manager';
import type { ApiInstance } from '../../api/types';

/**
 * Lazy load hooks factory chỉ khi ở client-side
 */
function getHooksFactory() {
  // Server-side: return null
  if (typeof window === 'undefined') {
    return null;
  }

  // Client-side: import hooks factory
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return require('../../api/hooks/factory').createHooks;
}

/**
 * Tạo hooks cho client-side, throw error nếu ở server-side
 */
function createHooksSafe(client: HttpClient) {
  const createHooks = getHooksFactory();

  if (!createHooks) {
    // Server-side: return no-op hooks
    const noOpHook = () => {
      throw new Error(
        'Hooks can only be used in client components. Use getFromService() for server-side.'
      );
    };

    return {
      useGet: noOpHook,
      usePost: noOpHook,
      usePut: noOpHook,
      usePatch: noOpHook,
      useDelete: noOpHook,
    };
  }

  // Client-side: tạo hooks
  return createHooks(client);
}

/**
 * Tạo API instance với authentication
 */
function createApiWithAuth(options: {
  baseUrl: string;
  name: string;
  defaultHeaders?: Record<string, string>;
  credentials?: RequestCredentials;
  clientOnly?: boolean;
}): ApiInstance {
  const client = new HttpClient({
    baseUrl: options.clientOnly
      ? () => {
          if (typeof window === 'undefined') {
            throw new Error(`${options.name} can only be used on the client.`);
          }

          return options.baseUrl;
        }
      : options.baseUrl,
    name: options.name,
    defaultHeaders: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...options.defaultHeaders,
    },
    getAuthToken: getAccessToken,
    refreshToken: refreshSessionToken,
    onUnauthorized: handleLogout,
    credentials: options.credentials,
  });

  // Lazy initialization của hooks - chỉ tạo khi được gọi từ client
  let hooksCache: ReturnType<typeof createHooksSafe> | null = null;

  const getHooksLazy = () => {
    if (!hooksCache) {
      hooksCache = createHooksSafe(client);
    }

    return hooksCache;
  };

  return {
    baseUrl: options.baseUrl,
    get: client.get.bind(client),
    post: client.post.bind(client),
    put: client.put.bind(client),
    patch: client.patch.bind(client),
    delete: client.delete.bind(client),
    request: client.request.bind(client),
    useGet: (...args) => getHooksLazy().useGet(...args),
    usePost: (...args) => getHooksLazy().usePost(...args),
    usePut: (...args) => getHooksLazy().usePut(...args),
    usePatch: (...args) => getHooksLazy().usePatch(...args),
    useDelete: (...args) => getHooksLazy().useDelete(...args),
  };
}

/**
 * Tạo API instance không có authentication (public APIs)
 */
export function createPublicApi(options: {
  baseUrl: string;
  name: string;
  defaultHeaders?: Record<string, string>;
}): ApiInstance {
  const client = new HttpClient({
    baseUrl: options.baseUrl,
    name: options.name,
    defaultHeaders: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...options.defaultHeaders,
    },
  });

  // Lazy initialization của hooks
  let hooksCache: ReturnType<typeof createHooksSafe> | null = null;

  const getHooksLazy = () => {
    if (!hooksCache) {
      hooksCache = createHooksSafe(client);
    }

    return hooksCache;
  };

  return {
    baseUrl: options.baseUrl,
    get: client.get.bind(client),
    post: client.post.bind(client),
    put: client.put.bind(client),
    patch: client.patch.bind(client),
    delete: client.delete.bind(client),
    request: client.request.bind(client),
    useGet: (...args) => getHooksLazy().useGet(...args),
    usePost: (...args) => getHooksLazy().usePost(...args),
    usePut: (...args) => getHooksLazy().usePut(...args),
    usePatch: (...args) => getHooksLazy().usePatch(...args),
    useDelete: (...args) => getHooksLazy().useDelete(...args),
  };
}

/**
 * Pre-configured API instances cho tất cả services
 */
export const apis = {
  /**
   * Job Seeker Service
   * User profiles, job history, applications
   */
  aiService: createApiWithAuth({
    baseUrl: publicEnv.aiApiDomain,
    name: 'AI API Service',
    defaultHeaders: {
      Accept: '*/*',
      'Accept-language': 'vi',
    },
    // Dùng 'omit' để tránh browser tự động thêm sec-fetch-storage-access header
    // Nếu cần gửi cookies, có thể đổi thành 'same-origin' hoặc 'include'
    credentials: 'omit',
  }),
  publicApi: createApiWithAuth({
    baseUrl: publicEnv.apiDomain,
    name: 'API Service',
    defaultHeaders: {
      'Accept-language': 'vi',
    },
  }),

  jobSeeker: createApiWithAuth({
    baseUrl: getServiceUrl(SERVICES.JOB_SEEKER, 'client'),
    name: 'Job Seeker Service',
    defaultHeaders: {
      'Accept-language': 'vi',
    },
  }),

  /**
   * Job Seekers Info Service
   * Additional user information
   */
  jobSeekerInfo: createApiWithAuth({
    baseUrl: getServiceUrl(SERVICES.JOB_SEEKERS_INFO, 'client'),
    name: 'Job Seekers Info Service',
    defaultHeaders: {
      'Accept-language': 'vi',
    },
  }),

  /**
   * Authentication Service
   * Login, logout, token management
   */
  authentication: createApiWithAuth({
    baseUrl: getServiceUrl(SERVICES.AUTHENTICATION, 'client'),
    name: 'Authentication Service',
  }),

  /**
   * Community Hub Service
   * Posts, comments, discussions
   */
  communityHub: createApiWithAuth({
    baseUrl: getServiceUrl(SERVICES.COMMUNITY_HUB, 'client'),
    name: 'Community Hub Service',
  }),

  /** Client-only notification transport; no server service mapping. */
  notification: createApiWithAuth({
    baseUrl: publicEnv.services.notification,
    name: 'Notification Service',
    clientOnly: true,
    defaultHeaders: {
      'Accept-language': 'vi',
    },
  }),
} as const;

/**
 * Export individual services for convenience
 */
export const publicApi = apis.publicApi;
export const aiServiceApi = apis.aiService;
export const jobSeekerApi = apis.jobSeeker;
export const jobSeekerInfoApi = apis.jobSeekerInfo;
export const authenticationApi = apis.authentication;
export const communityHubApi = apis.communityHub;
export const notificationApi = apis.notification;
